import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus, Search, Landmark, Archive, RotateCcw, MoreVertical, PoundSterling, Loader2,
  Briefcase, Pencil, Phone, Calendar, Mail, ExternalLink, Building2, ScrollText, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import API_BASE from '../../utils/apiBase';
import StaffingPipelineGuide from '../../components/portal/StaffingPipelineGuide';
import { CARE_ROLE_OPTIONS } from '../../config/careRoles';

const API = API_BASE;

const ORG_TYPES = [
  { value: 'care_home',                   label: 'Care Home' },
  { value: 'supported_living_provider',   label: 'Supported Living Provider' },
  { value: 'domiciliary_care_provider',   label: 'Domiciliary Care Provider' },
  { value: 'nhs_trust',                   label: 'NHS Trust' },
  { value: 'local_authority',             label: 'Local Authority' },
  { value: 'private_provider',            label: 'Private Provider' },
  { value: 'charity',                     label: 'Charity' },
  { value: 'other',                       label: 'Other' },
];

const ORG_TYPE_LABELS = Object.fromEntries(ORG_TYPES.map(({ value, label }) => [value, label]));

const ORG_SOURCES = [
  { value: 'cold_call', label: 'Cold call' },
  { value: 'phone', label: 'Phone / callback' },
  { value: 'email', label: 'Email' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'referral', label: 'Referral' },
  { value: 'website_contact', label: 'Website' },
  { value: 'other', label: 'Other' },
];

const RELATIONSHIP_STAGES = [
  { value: 'prospect', label: 'Prospect' },
  { value: 'active_client', label: 'Active client' },
  { value: 'inactive', label: 'Inactive' },
];

const SOURCE_LABELS = Object.fromEntries(ORG_SOURCES.map(({ value, label }) => [value, label]));
const STAGE_LABELS = Object.fromEntries(RELATIONSHIP_STAGES.map(({ value, label }) => [value, label]));

const STAGE_COLORS = {
  prospect: 'bg-amber-100 text-amber-800',
  active_client: 'bg-green-100 text-green-700',
  inactive: 'bg-gray-100 text-gray-500',
};

const EMPTY_FORM = {
  name: '',
  organisation_type: 'care_home',
  primary_contact_name: '',
  primary_contact_email: '',
  primary_contact_phone: '',
  postcode: '',
  notes: '',
  source: 'cold_call',
  relationship_stage: 'active_client',
  follow_up_at: '',
  social_handle: '',
};

function toDatetimeLocalValue(iso) {
  if (!iso) return '';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

function fmtFollowUp(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function isFollowUpOverdue(iso) {
  if (!iso) return false;
  return new Date(iso).getTime() <= Date.now();
}

const EMPTY_RATE_FORM = {
  role_name: '',
  rate_amount: '',
  vat_applicable: false,
  notes: '',
  effective_from: '',
};

function rateVersionState(rate) {
  const now = Date.now();
  const start = rate?.effective_from ? new Date(rate.effective_from).getTime() : Number.NEGATIVE_INFINITY;
  const end = rate?.effective_to ? new Date(rate.effective_to).getTime() : Number.POSITIVE_INFINITY;
  if (Number.isFinite(start) && start > now) return 'upcoming';
  if (now >= start && now < end) return 'current';
  return 'historical';
}

function formatRateDate(value) {
  if (!value) return 'open-ended';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Europe/London',
  });
}

function londonDateInputValue(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

export default function OrganisationsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [stageFilter, setStageFilter] = useState('all');
  const [followUpDueOnly, setFollowUpDueOnly] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editOrg, setEditOrg] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editSaving, setEditSaving] = useState(false);
  const [ratesOrg, setRatesOrg] = useState(null);
  const [chargeRates, setChargeRates] = useState([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [rateSaving, setRateSaving] = useState(false);
  const [rateForm, setRateForm] = useState(EMPTY_RATE_FORM);
  const [invitingId, setInvitingId] = useState(null);
  // Client terms + acceptance state
  const [termsOrg, setTermsOrg] = useState(null); // org whose acceptance detail is open
  const [termsDetail, setTermsDetail] = useState(null);
  const [termsDetailLoading, setTermsDetailLoading] = useState(false);
  const [showTermsManager, setShowTermsManager] = useState(false);
  const [termsVersions, setTermsVersions] = useState([]);
  const [termsVersionsLoading, setTermsVersionsLoading] = useState(false);
  const [termsForm, setTermsForm] = useState({ version: '', title: '', effective_date: '', content: '', publish: true });
  const [termsSaving, setTermsSaving] = useState(false);

  const loadTermsAcceptance = async (org) => {
    setTermsDetail(null);
    setTermsDetailLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/organisations/${org.id}/terms-acceptance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setTermsDetail(await res.json());
      else toast.error('Failed to load terms acceptance');
    } catch {
      toast.error('Failed to load terms acceptance');
    } finally {
      setTermsDetailLoading(false);
    }
  };

  const openTermsAcceptance = (org) => {
    // Defer opening until the dropdown menu has fully closed, otherwise Radix's
    // menu overlay can swallow pointer events over the dialog that opens on top.
    setTimeout(() => {
      setTermsOrg(org);
      loadTermsAcceptance(org);
    }, 60);
  };

  const loadTermsVersions = async () => {
    setTermsVersionsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/client-terms/versions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setTermsVersions((await res.json()).versions || []);
    } catch {
      toast.error('Failed to load terms versions');
    } finally {
      setTermsVersionsLoading(false);
    }
  };

  const openTermsManager = () => {
    setShowTermsManager(true);
    setTermsForm({ version: '', title: '', effective_date: '', content: '', publish: true });
    loadTermsVersions();
  };

  const createTermsVersion = async (e) => {
    e.preventDefault();
    if (!termsForm.version.trim() || !termsForm.title.trim() || !termsForm.content.trim()) {
      toast.error('Version, title and content are required');
      return;
    }
    setTermsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/client-terms/versions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          version: termsForm.version.trim(),
          title: termsForm.title.trim(),
          effective_date: termsForm.effective_date.trim() || undefined,
          content: termsForm.content,
          publish: !!termsForm.publish,
        }),
      });
      if (res.ok) {
        toast.success(termsForm.publish ? 'Terms version published' : 'Terms version saved as draft');
        setTermsForm({ version: '', title: '', effective_date: '', content: '', publish: true });
        loadTermsVersions();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save terms version');
      }
    } catch {
      toast.error('Failed to save terms version');
    } finally {
      setTermsSaving(false);
    }
  };

  const publishTermsVersion = async (versionId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/client-terms/versions/${versionId}/publish`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Version is now current');
        loadTermsVersions();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to publish');
      }
    } catch {
      toast.error('Failed to publish');
    }
  };

  const fetchOrgs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/organisations?include_inactive=${includeInactive}`;
      if (search.trim()) url += `&search=${encodeURIComponent(search.trim())}`;
      if (stageFilter !== 'all') url += `&relationship_stage=${stageFilter}`;
      if (followUpDueOnly) url += '&follow_up_due=true';
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setOrgs(data.organisations || []);
      } else {
        toast.error('Failed to load organisations');
      }
    } catch {
      toast.error('Failed to load organisations');
    } finally {
      setLoading(false);
    }
  }, [search, includeInactive, stageFilter, followUpDueOnly]);

  useEffect(() => {
    const stage = searchParams.get('stage');
    const followUp = searchParams.get('follow_up_due');
    if (stage) setStageFilter(stage);
    if (followUp === '1') setFollowUpDueOnly(true);
  }, [searchParams]);

  useEffect(() => {
    const orgId = searchParams.get('org');
    if (!orgId || !orgs.length) return;
    const match = orgs.find((o) => o.id === orgId);
    if (match && !editOrg) openEdit(match);
  }, [orgs, searchParams, editOrg]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(fetchOrgs, search.trim() ? 350 : 0);
    return () => clearTimeout(timer);
  }, [fetchOrgs, search]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.organisation_type) {
      toast.error('Name and organisation type are required');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/organisations`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...form,
          follow_up_at: form.follow_up_at ? new Date(form.follow_up_at).toISOString() : undefined,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.possible_duplicate) {
          toast.warning(`Created, but "${data.possible_duplicate.name}" may already exist — check for duplicates.`);
        } else {
          toast.success(`Prospect logged: ${data.organisation.name}`);
        }
        setShowCreateDialog(false);
        setForm(EMPTY_FORM);
        fetchOrgs();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to create organisation');
      }
    } catch {
      toast.error('Failed to create organisation');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (org) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/organisations/${org.id}/archive`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success(`${org.name} archived`);
        fetchOrgs();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to archive');
      }
    } catch {
      toast.error('Failed to archive organisation');
    }
  };

  const fetchChargeRates = async (orgId) => {
    setRatesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/charge-rates?organisation_id=${encodeURIComponent(orgId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setChargeRates(data.charge_rates || []);
      } else if (res.status === 403) {
        toast.error('Charge rates are admin-only');
        setChargeRates([]);
      } else {
        toast.error('Failed to load charge rates');
      }
    } catch {
      toast.error('Failed to load charge rates');
    } finally {
      setRatesLoading(false);
    }
  };

  const openChargeRates = (org) => {
    if (!isAdmin()) {
      toast.error('Charge rates are admin-only — ask your finance admin to set client billing rates');
      return;
    }
    setRatesOrg(org);
    // Default effective from to today for non-tech users (easy to change)
    const today = londonDateInputValue();
    setRateForm({ ...EMPTY_RATE_FORM, effective_from: today });
    fetchChargeRates(org.id);
  };

  const handleAddChargeRate = async (e) => {
    e.preventDefault();
    if (!ratesOrg) return;

    const trimmedRole = rateForm.role_name.trim();
    const amount = parseFloat(rateForm.rate_amount);

    if (!trimmedRole) {
      toast.error('Role name is required (must match the role used on shifts for this client)');
      return;
    }
    if (Number.isNaN(amount) || amount <= 0) {
      toast.error('Enter a positive hourly rate (what this client will be billed)');
      return;
    }

    // Client-side duplicate check for active rates (nice UX for non-tech users)
    const currentRate = chargeRates.find(
      (r) => rateVersionState(r) === 'current'
        && String(r.role_name || '').toLowerCase() === trimmedRole.toLowerCase()
    );
    if (currentRate) {
      const proceed = window.confirm(
        `The current rate for "${trimmedRole}" is £${Number(currentRate.rate_amount).toFixed(2)}/hr.\n\n` +
        `The new rate will start on the effective date you chose. Older shifts keep the rate that applied on their service date.\n\nContinue?`
      );
      if (!proceed) return;
    }

    setRateSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        organisation_id: ratesOrg.id,
        role_name: trimmedRole,
        rate_amount: amount,
        vat_applicable: rateForm.vat_applicable,
        notes: rateForm.notes.trim() || undefined,
      };
      if (rateForm.effective_from) {
        payload.effective_from = rateForm.effective_from;
      }

      const res = await fetch(`${API}/charge-rates`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success('Charge rate added / versioned');
        setRateForm(EMPTY_RATE_FORM);
        fetchChargeRates(ratesOrg.id);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to add charge rate');
      }
    } catch {
      toast.error('Failed to add charge rate');
    } finally {
      setRateSaving(false);
    }
  };

  const openEdit = (org) => {
    setEditOrg(org);
    setEditForm({
      name: org.name || '',
      organisation_type: org.organisation_type || '',
      primary_contact_name: org.primary_contact_name || '',
      primary_contact_email: org.primary_contact_email || '',
      primary_contact_phone: org.primary_contact_phone || '',
      postcode: org.postcode || '',
      notes: org.notes || '',
      source: org.source || 'other',
      relationship_stage: org.relationship_stage || 'prospect',
      follow_up_at: toDatetimeLocalValue(org.follow_up_at),
      social_handle: org.social_handle || '',
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editOrg) return;
    setEditSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/organisations/${editOrg.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...editForm,
          follow_up_at: editForm.follow_up_at ? new Date(editForm.follow_up_at).toISOString() : null,
        }),
      });
      if (res.ok) {
        toast.success('Organisation updated');
        setEditOrg(null);
        fetchOrgs();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update');
      }
    } catch {
      toast.error('Failed to update organisation');
    } finally {
      setEditSaving(false);
    }
  };

  const logContacted = async (org) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/organisations/${org.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ last_contacted_at: new Date().toISOString() }),
      });
      if (res.ok) {
        toast.success(`Logged contact with ${org.name}`);
        fetchOrgs();
      }
    } catch {
      toast.error('Failed to log contact');
    }
  };

  const startStaffingRequest = (org) => {
    navigate(`/portal/staffing-requests?org=${org.id}&create=1`);
  };

  const inviteClientHub = async (org) => {
    if (!org.primary_contact_email?.trim()) {
      toast.error('Set primary contact email first');
      openEdit(org);
      return;
    }
    setInvitingId(org.id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/organisations/${org.id}/invite-client-hub`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success(`Client Hub invite sent to ${org.primary_contact_email}`);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to send invite');
      }
    } catch {
      toast.error('Failed to send invite');
    } finally {
      setInvitingId(null);
    }
  };

  const handleRestore = async (org) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/organisations/${org.id}/restore`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success(`${org.name} restored`);
        fetchOrgs();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to restore');
      }
    } catch {
      toast.error('Failed to restore organisation');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const activeCount = orgs.filter((o) => o.is_active !== false).length;
  const clientCount = orgs.filter((o) => (o.relationship_stage || 'active_client') === 'active_client').length;

  return (
    <div className="space-y-6" data-testid="organisations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Client organisations</h1>
          <p className="text-sm text-text-muted mt-1">
            Confirmed clients only — charge rates, shifts, and invoicing. Log new prospects in Sales CRM first.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openTermsManager} data-testid="manage-terms-btn">
            <ScrollText className="h-4 w-4 mr-2" />
            Client terms
          </Button>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-org-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Organisation
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add client organisation</DialogTitle>
              <DialogDescription>
                For confirmed clients only. Chasing a care home? Use Sales CRM instead, then convert when they commit.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="org-name">Name *</Label>
                <Input
                  id="org-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Sunrise Care Home"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-type">Organisation Type *</Label>
                <select
                  id="org-type"
                  value={form.organisation_type}
                  onChange={(e) => setForm({ ...form, organisation_type: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                  required
                >
                  <option value="">Select type…</option>
                  {ORG_TYPES.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="org-contact-name">Primary Contact</Label>
                  <Input
                    id="org-contact-name"
                    value={form.primary_contact_name}
                    onChange={(e) => setForm({ ...form, primary_contact_name: e.target.value })}
                    placeholder="Full name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="org-contact-phone">Contact Phone</Label>
                  <Input
                    id="org-contact-phone"
                    value={form.primary_contact_phone}
                    onChange={(e) => setForm({ ...form, primary_contact_phone: e.target.value })}
                    placeholder="07xxx xxxxxx"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="org-source">How did you reach them? *</Label>
                  <select
                    id="org-source"
                    value={form.source}
                    onChange={(e) => setForm({ ...form, source: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                    required
                  >
                    {ORG_SOURCES.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="org-follow-up">Follow-up date/time</Label>
                  <Input
                    id="org-follow-up"
                    type="datetime-local"
                    value={form.follow_up_at}
                    onChange={(e) => setForm({ ...form, follow_up_at: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-social">Instagram / social handle</Label>
                <Input
                  id="org-social"
                  value={form.social_handle}
                  onChange={(e) => setForm({ ...form, social_handle: e.target.value })}
                  placeholder="@carehomekent"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="org-contact-email">Contact Email</Label>
                  <Input
                    id="org-contact-email"
                    type="email"
                    value={form.primary_contact_email}
                    onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="org-postcode">Postcode</Label>
                  <Input
                    id="org-postcode"
                    value={form.postcode}
                    onChange={(e) => setForm({ ...form, postcode: e.target.value })}
                    placeholder="SW1A 1AA"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-notes">Notes</Label>
                <Textarea
                  id="org-notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  placeholder="Optional notes"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowCreateDialog(false); setForm(EMPTY_FORM); }}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? 'Creating…' : 'Create Organisation'}
                </Button>
              </div>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      <StaffingPipelineGuide currentStep={3} />

      <div className="p-4 rounded-xl bg-amber-50 border border-amber-100 text-sm text-amber-900">
        Cold calls and Instagram DMs belong in{' '}
        <button type="button" className="font-semibold underline" onClick={() => navigate('/portal/sales-leads')}>
          Sales CRM
        </button>
        . Add an organisation here only after they agree to use your services (or use Convert to client from CRM).
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-xl bg-white border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Landmark className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-primary">{orgs.length}</p>
              <p className="text-xs text-text-muted">Total Shown</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl bg-white border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100">
              <Landmark className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-primary">{activeCount}</p>
              <p className="text-xs text-text-muted">Active</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl bg-white border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100">
              <Landmark className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-primary">{clientCount}</p>
              <p className="text-xs text-text-muted">Active clients</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
          <Input
            placeholder="Search by name, contact, or postcode…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
        >
          <option value="all">All stages</option>
          {RELATIONSHIP_STAGES.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-text-muted cursor-pointer select-none">
          <input
            type="checkbox"
            checked={followUpDueOnly}
            onChange={(e) => setFollowUpDueOnly(e.target.checked)}
            className="rounded"
          />
          Follow-up due
        </label>
        <label className="flex items-center gap-2 text-sm text-text-muted cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="rounded"
          />
          Show archived
        </label>
      </div>

      {/* Rates explanation for non-technical users — reinforced for pipeline predictability */}
      <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm">
        <p className="font-medium text-blue-900">Billing rates vs worker pay (part of the predictable financial pipeline)</p>
        <p className="text-blue-800 mt-1">
          <strong>Charge rates</strong> (set here per client/organisation) = what the client pays <em>you</em> for each role. Used for client invoices. These rates power the “Financially Closed” status when combined with payroll.
          <br />
          <strong>Worker pay rates</strong> = what you pay the staff (set per employee in the Employees section, or through HCA/Nurse defaults in Settings). These are separate from client billing and feed payroll only.
        </p>
        <p className="text-blue-700 text-xs mt-1">Tip: Always set charge rates for a client before generating invoices for them. Role names must match exactly what you use on shifts for that client — this is required for the pipeline to reach “Financially Closed” (payroll + invoiced). See Shifts and Invoices for the rest of the flow.</p>
      </div>

      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-900">
        <strong>Client Hub:</strong> Care homes see upcoming cover, worker compliance badges, sites, and invoices at{' '}
        <a href="/client/login" target="_blank" rel="noopener noreferrer" className="underline font-medium inline-flex items-center gap-1">
          /client/login <ExternalLink className="h-3 w-3" />
        </a>.
        Set <em>primary contact email</em>, then use <strong>Send Client Hub invite</strong> in the row menu.
      </div>

      {/* List */}
      {orgs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
          <Landmark className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-primary mb-2">No Organisations</h3>
          <p className="text-sm text-text-muted mb-4">
            Add your first organisation to get started
          </p>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Organisation
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Organisation</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Stage</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Source</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Contact</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Follow-up</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Rates</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {orgs.map((org) => (
                  <tr key={org.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Landmark className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <button
                            type="button"
                            onClick={() => navigate(`/portal/organisations/${org.id}`)}
                            className="font-medium text-text-primary text-sm text-left hover:text-primary hover:underline"
                          >
                            {org.name}
                          </button>
                          {org.notes && (
                            <p className="text-xs text-text-muted mt-0.5 max-w-xs truncate">{org.notes}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STAGE_COLORS[org.relationship_stage || 'prospect'] || STAGE_COLORS.prospect}`}>
                        {STAGE_LABELS[org.relationship_stage || 'prospect'] || 'Prospect'}
                      </span>
                      {!org.is_verified && org.relationship_stage !== 'inactive' && (
                        <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] bg-amber-50 text-amber-700">Unverified</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-xs text-text-muted">
                      {SOURCE_LABELS[org.source] || org.source || '—'}
                      {org.social_handle && (
                        <p className="text-[10px] mt-0.5 truncate max-w-[100px]">{org.social_handle}</p>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-text-primary">{org.primary_contact_name || '—'}</p>
                      {org.primary_contact_phone && (
                        <a href={`tel:${org.primary_contact_phone.replace(/\s/g, '')}`} className="text-xs text-primary hover:underline block">
                          {org.primary_contact_phone}
                        </a>
                      )}
                      {org.primary_contact_email && (
                        <a href={`mailto:${org.primary_contact_email}`} className="text-xs text-text-muted hover:underline block truncate max-w-[140px]">
                          {org.primary_contact_email}
                        </a>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <span className={isFollowUpOverdue(org.follow_up_at) ? 'text-red-600 font-medium' : 'text-text-muted'}>
                        {fmtFollowUp(org.follow_up_at)}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      {isAdmin() ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs h-7 px-2"
                          onClick={() => openChargeRates(org)}
                        >
                          <PoundSterling className="h-3 w-3 mr-1" />
                          Manage rates
                        </Button>
                      ) : (
                        <span className="text-[10px] text-text-muted">Admin sets rates</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem className="cursor-pointer" onClick={() => navigate(`/portal/organisations/${org.id}`)}>
                            <Building2 className="h-4 w-4 mr-2" />
                            Open client record
                          </DropdownMenuItem>
                          <DropdownMenuItem className="cursor-pointer" onClick={() => openEdit(org)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit / notes
                          </DropdownMenuItem>
                          <DropdownMenuItem className="cursor-pointer" onClick={() => logContacted(org)}>
                            <Phone className="h-4 w-4 mr-2" />
                            Log contacted now
                          </DropdownMenuItem>
                          <DropdownMenuItem className="cursor-pointer" onClick={() => startStaffingRequest(org)}>
                            <Briefcase className="h-4 w-4 mr-2" />
                            New staffing request
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="cursor-pointer"
                            onClick={() => inviteClientHub(org)}
                            disabled={invitingId === org.id}
                          >
                            <Mail className="h-4 w-4 mr-2" />
                            {invitingId === org.id ? 'Sending invite…' : 'Send Client Hub invite'}
                          </DropdownMenuItem>
                          <DropdownMenuItem className="cursor-pointer" onClick={() => openTermsAcceptance(org)}>
                            <ScrollText className="h-4 w-4 mr-2" />
                            Terms acceptance
                          </DropdownMenuItem>
                          {org.is_active !== false ? (
                            <DropdownMenuItem
                              className="text-amber-600 cursor-pointer"
                              onClick={() => handleArchive(org)}
                            >
                              <Archive className="h-4 w-4 mr-2" />
                              Archive
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              className="text-green-600 cursor-pointer"
                              onClick={() => handleRestore(org)}
                            >
                              <RotateCcw className="h-4 w-4 mr-2" />
                              Restore
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-gray-50 bg-gray-50">
            <p className="text-xs text-text-muted">
              {orgs.length} organisation{orgs.length !== 1 ? 's' : ''}
              {!includeInactive ? ' — active only' : ''}
            </p>
          </div>
        </div>
      )}

      {/* Top-level help for non-technical users on rates */}
      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
        <strong>Quick guide for billing:</strong> Use "Charge rates" for what each client pays you per role (for their invoices). 
        Worker pay rates (what staff earn) live on employee records or as HCA/Nurse defaults in Settings. Set client charge rates here before invoicing that organisation.
      </div>

      <Dialog open={!!editOrg} onOpenChange={(open) => { if (!open) setEditOrg(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit — {editOrg?.name}</DialogTitle>
            <DialogDescription>Update contact details, notes, follow-up, and pipeline stage.</DialogDescription>
          </DialogHeader>
          {editOrg && (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Stage</Label>
                  <select
                    value={editForm.relationship_stage}
                    onChange={(e) => setEditForm({ ...editForm, relationship_stage: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
                  >
                    {RELATIONSHIP_STAGES.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Source</Label>
                  <select
                    value={editForm.source}
                    onChange={(e) => setEditForm({ ...editForm, source: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
                  >
                    {ORG_SOURCES.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Follow-up</Label>
                <Input
                  type="datetime-local"
                  value={editForm.follow_up_at}
                  onChange={(e) => setEditForm({ ...editForm, follow_up_at: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact name</Label>
                  <Input
                    value={editForm.primary_contact_name}
                    onChange={(e) => setEditForm({ ...editForm, primary_contact_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={editForm.primary_contact_phone}
                    onChange={(e) => setEditForm({ ...editForm, primary_contact_phone: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editForm.primary_contact_email}
                  onChange={(e) => setEditForm({ ...editForm, primary_contact_email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Social handle</Label>
                <Input
                  value={editForm.social_handle}
                  onChange={(e) => setEditForm({ ...editForm, social_handle: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={4}
                  placeholder="Call notes, manager name, shift needs…"
                />
              </div>
              {editOrg.last_contacted_at && (
                <p className="text-xs text-text-muted">
                  Last contacted: {fmtFollowUp(editOrg.last_contacted_at)}
                </p>
              )}
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setEditOrg(null)} disabled={editSaving}>
                  Cancel
                </Button>
                <Button type="submit" disabled={editSaving}>
                  {editSaving ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!ratesOrg} onOpenChange={(open) => { if (!open) setRatesOrg(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Charge rates — {ratesOrg?.name}</DialogTitle>
            <DialogDescription>
              These are the rates you bill the <strong>client organisation</strong> (e.g. council or care home) for each role.
              They are completely separate from what you pay the worker (pay rates are set on the employee profile or through HCA/Nurse defaults in Settings).
            </DialogDescription>
          </DialogHeader>

          {ratesLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {chargeRates.length === 0 ? (
                <p className="text-sm text-text-muted py-2">No charge rates yet for this organisation. Add one below to enable client invoicing for this client.</p>
              ) : (
                <div className="mb-4">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-xs font-semibold text-text-muted">Rate timeline</p>
                    <p className="text-[10px] text-text-muted">Invoice rate is chosen by shift service date</p>
                  </div>
                  <ul className="divide-y divide-gray-100 border rounded-lg max-h-64 overflow-y-auto">
                    {chargeRates.map((rate) => {
                      const state = rateVersionState(rate);
                      const badgeClass = state === 'current'
                        ? 'bg-emerald-50 text-emerald-700'
                        : state === 'upcoming'
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-slate-100 text-slate-600';
                      return (
                        <li key={rate.id} className="px-3 py-2.5 text-sm">
                          <div className="flex justify-between gap-3">
                            <span>
                              <span className="font-medium">{rate.role_name || 'default'}</span>
                              <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${badgeClass}`}>
                                {state === 'current' ? 'Current' : state === 'upcoming' ? 'Upcoming' : 'Historical'}
                              </span>
                              {rate.vat_applicable && (
                                <span className="ml-2 text-xs text-blue-600">+ VAT</span>
                              )}
                            </span>
                            <span className="font-semibold whitespace-nowrap">£{Number(rate.rate_amount).toFixed(2)}/hr</span>
                          </div>
                          <div className="text-[10px] text-text-muted mt-1">
                            {formatRateDate(rate.effective_from)} → {rate.effective_to ? formatRateDate(rate.effective_to) : 'open-ended'}
                          </div>
                          {rate.notes && (
                            <div className="text-[10px] text-text-muted truncate">Note: {rate.notes}</div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              <form onSubmit={handleAddChargeRate} className="space-y-3 border-t pt-4">
                <div className="text-xs bg-blue-50 border border-blue-100 rounded p-2 text-blue-800">
                  These rates are what <strong>this client pays you</strong>. Use the same HCA/Nurse role names used on shifts. Worker pay rates (what staff actually earn) are set separately on each employee profile or as tenant defaults in Settings.
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rate-role">Role name * <span className="text-text-muted font-normal">(must match shift roles exactly)</span></Label>
                  <Select
                    value={rateForm.role_name || ''}
                    onValueChange={(value) => setRateForm({ ...rateForm, role_name: value })}
                  >
                    <SelectTrigger id="rate-role">
                      <SelectValue placeholder="Select role..." />
                    </SelectTrigger>
                    <SelectContent>
                      {CARE_ROLE_OPTIONS.map((role) => (
                        <SelectItem key={role} value={role}>{role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-text-muted">Only these standard roles are allowed. This prevents mismatches that break invoicing and the financial pipeline.</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="rate-amount">Hourly rate client pays (£) *</Label>
                    <Input
                      id="rate-amount"
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={rateForm.rate_amount}
                      onChange={(e) => setRateForm({ ...rateForm, rate_amount: e.target.value })}
                      placeholder="28.50"
                      required
                    />
                  </div>
                  <div className="flex items-end pb-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={rateForm.vat_applicable}
                        onChange={(e) => setRateForm({ ...rateForm, vat_applicable: e.target.checked })}
                        className="rounded"
                      />
                      VAT applicable (20%)
                    </label>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rate-effective">Effective from (optional)</Label>
                  <Input
                    id="rate-effective"
                    type="date"
                    value={rateForm.effective_from}
                    onChange={(e) => setRateForm({ ...rateForm, effective_from: e.target.value })}
                  />
                  <p className="text-[10px] text-text-muted">The new price applies only to shifts on/after this date. Future dates schedule a change; older shifts keep the historical rate that applied when the work happened.</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rate-notes">Notes (optional)</Label>
                  <Input
                    id="rate-notes"
                    value={rateForm.notes}
                    onChange={(e) => setRateForm({ ...rateForm, notes: e.target.value })}
                    placeholder="e.g. Contract ref XYZ-2026 or annual review"
                  />
                </div>

                <Button type="submit" disabled={rateSaving} className="w-full">
                  {rateSaving ? 'Saving…' : 'Add / Update charge rate'}
                </Button>
                <p className="text-[10px] text-text-muted">CareOps automatically closes the previous version at the new effective date and keeps the full rate history. It will reject duplicate/overlapping start dates rather than guess.</p>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Per-organisation terms acceptance detail */}
      <Dialog open={!!termsOrg} onOpenChange={(open) => { if (!open) { setTermsOrg(null); setTermsDetail(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="terms-acceptance-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScrollText className="h-5 w-5 text-primary" />
              Terms acceptance — {termsOrg?.name}
            </DialogTitle>
            <DialogDescription>Who accepted the current client terms, and the full acceptance history.</DialogDescription>
          </DialogHeader>
          {termsDetailLoading ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : !termsDetail ? (
            <p className="text-sm text-text-muted py-6">Could not load acceptance details.</p>
          ) : (
            <div className="space-y-4">
              {termsDetail.current ? (
                <div className="rounded-lg border border-gray-100 p-3">
                  <p className="text-xs text-text-muted">Current terms</p>
                  <p className="font-medium text-sm">{termsDetail.current.title} · v{termsDetail.current.version}</p>
                  {termsDetail.has_accepted_current ? (
                    <div className="mt-2 flex items-start gap-2 text-sm text-emerald-700">
                      <CheckCircle2 className="h-4 w-4 mt-0.5" />
                      <span>
                        Accepted by <strong>{termsDetail.current_acceptance?.accepted_by_name || termsDetail.current_acceptance?.accepted_by_email || '—'}</strong>
                        {termsDetail.current_acceptance?.accepted_at ? ` on ${new Date(termsDetail.current_acceptance.accepted_at).toLocaleString('en-GB')}` : ''}
                        {termsDetail.current_acceptance?.client_contact_id ? ` (contact ${termsDetail.current_acceptance.client_contact_id})` : ''}
                      </span>
                    </div>
                  ) : (
                    <div className="mt-2 flex items-center gap-2 text-sm text-amber-700">
                      <AlertCircle className="h-4 w-4" />
                      <span>Not yet accepted for the current version</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-text-muted">No current client terms have been published yet.</p>
              )}

              <div>
                <p className="text-xs font-semibold text-text-muted mb-2">Acceptance history</p>
                {(termsDetail.history || []).length === 0 ? (
                  <p className="text-sm text-text-muted">No acceptances recorded.</p>
                ) : (
                  <div className="space-y-2">
                    {termsDetail.history.map((h) => (
                      <div key={h.id} className="rounded-lg bg-gray-50 border border-gray-100 p-2.5 text-xs">
                        <p className="font-medium text-text-primary">v{h.terms_version} · {h.terms_title}</p>
                        <p className="text-text-muted mt-0.5">
                          {h.accepted_by_name || h.accepted_by_email || '—'}
                          {h.accepted_at ? ` · ${new Date(h.accepted_at).toLocaleString('en-GB')}` : ''}
                        </p>
                        {h.evidence?.ip_address && (
                          <p className="text-[10px] text-text-muted mt-0.5">IP {h.evidence.ip_address}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Client terms version manager */}
      <Dialog open={showTermsManager} onOpenChange={setShowTermsManager}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="terms-manager-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScrollText className="h-5 w-5 text-primary" />
              Client terms versions
            </DialogTitle>
            <DialogDescription>
              Publish a version to make it the current terms clients must accept. Legal wording is supplied and reviewed separately — paste the approved text below.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div>
              <p className="text-xs font-semibold text-text-muted mb-2">Existing versions</p>
              {termsVersionsLoading ? (
                <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
              ) : termsVersions.length === 0 ? (
                <p className="text-sm text-text-muted">No versions yet — create the first one below.</p>
              ) : (
                <div className="space-y-2">
                  {termsVersions.map((v) => (
                    <div key={v.id} className="flex items-center justify-between rounded-lg border border-gray-100 p-2.5">
                      <div>
                        <p className="text-sm font-medium">v{v.version} · {v.title}
                          {v.status === 'active' && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">Current</span>}
                          {v.status === 'draft' && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">Draft</span>}
                          {v.status === 'archived' && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Archived</span>}
                        </p>
                        <p className="text-[10px] text-text-muted mt-0.5">{v.effective_date ? `Effective ${v.effective_date}` : 'No effective date'}</p>
                      </div>
                      {v.status !== 'active' && (
                        <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => publishTermsVersion(v.id)}>
                          Make current
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <form onSubmit={createTermsVersion} className="space-y-3 border-t border-gray-100 pt-4">
              <p className="text-sm font-semibold text-text-primary">New version</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Version *</Label>
                  <Input value={termsForm.version} onChange={(e) => setTermsForm({ ...termsForm, version: e.target.value })} placeholder="e.g. 1.0" />
                </div>
                <div className="space-y-1.5">
                  <Label>Effective date</Label>
                  <Input type="date" value={termsForm.effective_date} onChange={(e) => setTermsForm({ ...termsForm, effective_date: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Title *</Label>
                <Input value={termsForm.title} onChange={(e) => setTermsForm({ ...termsForm, title: e.target.value })} placeholder="e.g. Client Service Terms" />
              </div>
              <div className="space-y-1.5">
                <Label>Terms content (approved legal wording) *</Label>
                <Textarea rows={6} value={termsForm.content} onChange={(e) => setTermsForm({ ...termsForm, content: e.target.value })} placeholder="Paste the reviewed terms text here" />
              </div>
              <label className="flex items-center gap-2 text-sm text-text-muted">
                <input type="checkbox" checked={termsForm.publish} onChange={(e) => setTermsForm({ ...termsForm, publish: e.target.checked })} />
                Publish immediately as the current terms
              </label>
              <Button type="submit" disabled={termsSaving} data-testid="save-terms-version-btn">
                {termsSaving ? 'Saving…' : (termsForm.publish ? 'Publish version' : 'Save draft')}
              </Button>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}